'use client';

import { useState } from 'react';

import { useApi } from '@/hooks/useApi';
import { UseFormActions } from '@/hooks/useForm';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/components/atoms/RichTextField/RichTextFieldStrapi';

import { BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import { apiEndpoints } from '@/constant/urls';
import { HeroBackground } from '@/features/HomepageHero/components/AnimatedBackground';
import { sendGAEvent } from '@/integrations/trackingScripts';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import {
  ApplicationForm,
  ApplicationFormSchema,
} from './components/ApplicationForm';

export type JobApplicationFormProps = {
  job: {
    role: string;
    hrEmail: string;
    title?: string;
    description?: RichTextFieldStrapiProps;
  };
};

export function JobApplicationForm({ job }: JobApplicationFormProps) {
  const { title, description } = job;
  const { texts } = useTranslation();
  const [submitSuccessCount, setSubmitSuccessCount] = useState(0);
  const [submitFailedCount, setSubmitFailedCount] = useState(0);
  const [resumeSubmitError, setResumeSubmitError] = useState<string | null>(
    null
  );
  const {
    loading: isSubmitting,
    makeApiCall: submit,
  } = useApi({
    url: apiEndpoints.jobApplication,
    method: 'POST',
  });

  async function handleSubmit(
    data: ApplicationFormSchema,
    action: UseFormActions
  ) {
    const formData = new FormData();
    formData.set('role', job.role);
    formData.set('hr_email', job.hrEmail);
    formData.set('full_name', data.fullName);
    formData.set('email', data.email);
    const phone = data.phoneNumber
      ? `${data.countryCode}${data.phoneNumber}`
      : '+1';
    formData.set('phone', phone);
    if (data.linkedInProfile) {
      formData.set('profile_link', data.linkedInProfile);
    }
    if (data.information) {
      formData.set('info', data.information);
    }
    if (data.resume) {
      formData.set('file', data.resume);
    }

    try {
      const response = await submit({
        body: formData,
      });
      if (response?.error) {
        if (response.status === 413 || response.status === 400) {
          setResumeSubmitError(
            texts.job_application.error.please_try_a_different_file
          );
        } else {
          setSubmitFailedCount((count) => count + 1);
        }
        return;
      }

      setResumeSubmitError(null);

      setSubmitSuccessCount((count) => count + 1);
      sendGAEvent({
        event: 'ApplicationForm_submitted',
      });
      action.resetForm();
    } catch (error) {
      console.error(error);
      setSubmitFailedCount((count) => count + 1);
    }
  }
  return (
    <div className='relative'>
      <div className='absolute inset-0 bottom-0 z-0 hidden items-end justify-center overflow-hidden md:flex'>
        <HeroBackground />
      </div>
      <BlockWrapper
        data-tracker-section={SECTION_NAMES.JOB_APPLICATION_FORM}
        contentFit='full'
      >
        <div className='relative z-10 w-full md:bg-gray-25/50 md:px-5 md:py-8 md:backdrop-blur-[4px]'>
          <div className='max-w-[860px] rounded border border-[#D6D6D6] bg-base-white mx-auto px-5 py-8 md:p-16'>
            <BlockHeaderContent
              title={title}
              alignment='left'
              classNames={{
                header: cn(description ? '!mb-4' : 'mb-8 md:mb-10 text-center'),
              }}
              subtitle={
                description ? (
                  <RichTextFieldStrapi block={description} />
                ) : undefined
              }
            >
              <div>
                <ApplicationForm
                  onSubmit={handleSubmit}
                  loading={isSubmitting}
                  submitSucceeded={submitSuccessCount}
                  submitFailed={submitFailedCount}
                  resumeSubmitError={resumeSubmitError ?? undefined}
                  onResumeChange={() => setResumeSubmitError(null)}
                />
              </div>
            </BlockHeaderContent>
          </div>
        </div>
      </BlockWrapper>
    </div>
  );
}
